import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'

// SEO Components
import SEOHead from './components/SEOHead'
import StructuredData from './components/StructuredData'
import PublicDataLoader from './components/PublicDataLoader'

// Main Website Components
import Header from './components/Header'
import Hero from './components/Hero'
import AnnouncementNews from './components/AnnouncementNews'
import AchievementsHome from './components/AchievementsHome'
import Academics from './components/Academics'
import Gallery from './components/Gallery'
import Admissions from './components/Admissions'
import Contact from './components/Contact'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import Popup from './components/Popup'

// Page Components
const AboutTrust = lazy(() => import('./components/pages/AboutTrust'))
const CampusOverview = lazy(() => import('./components/pages/CampusOverview'))
const SkillBasedActivity = lazy(() => import('./components/pages/SkillBasedActivity'))
const AcademicPrograms = lazy(() => import('./components/pages/AcademicPrograms'))
const StaffDetails = lazy(() => import('./components/pages/StaffDetails'))
const InstitutionsPage = lazy(() => import('./components/pages/InstitutionsPage'))
const EventsCelebrations = lazy(() => import('./components/pages/EventsCelebrations'))
const NewsUpdates = lazy(() => import('./components/pages/NewsUpdates'))
const AchievementsAwards = lazy(() => import('./components/pages/AchievementsAwards'))
const SocialActivities = lazy(() => import('./components/pages/SocialActivities'))
const AlumniCorner = lazy(() => import('./components/pages/AlumniCorner'))
const AlumniStories = lazy(() => import('./components/pages/AlumniStories'))
const CampusDirectory = lazy(() => import('./components/pages/CampusDirectory'))
const AnnualPlanner = lazy(() => import('./components/pages/AnnualPlanner'))
const ContactUs = lazy(() => import('./components/pages/ContactUs'))
const NotFound = lazy(() => import('./components/pages/NotFound'))

// Admin Components
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'))
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'))
const Dashboard = lazy(() => import('./components/admin/Dashboard'))
const AdmissionManagement = lazy(() => import('./components/admin/Admissions'))
const GalleryManagement = lazy(() => import('./components/admin/GalleryManagement'))
const ManagementTeam = lazy(() => import('./components/admin/ManagementTeam'))
const SliderManagement = lazy(() => import('./components/admin/SliderManagement'))
const Messages = lazy(() => import('./components/admin/Messages'))
const ImportantDates = lazy(() => import('./components/admin/ImportantDates'))
const NewsManagement = lazy(() => import('./components/admin/NewsManagement'))
const AnnouncementManagement = lazy(() => import('./components/admin/AnnouncementManagement'))
const AchievementsManagement = lazy(() => import('./components/admin/AchievementsManagement'))
const StaffManagement = lazy(() => import('./components/admin/StaffManagement'))
const AnnualPlannerManagement = lazy(() => import('./components/admin/AnnualPlannerManagement'))
const CampusDocumentManagement = lazy(() => import('./components/admin/CampusDocumentManagement'))
const AlumniNetworkManagement = lazy(() => import('./components/admin/AlumniNetworkManagement'))
const PopupManagement = lazy(() => import('./components/admin/PopupManagement'))
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'))

const HOME_CANONICAL = 'https://dhgc.org.in/'

// Main website component
const MainWebsite = () => {
  return (
    <div className="min-h-screen bg-neutral-bg-light">
      <SEOHead canonical={HOME_CANONICAL} />
      <StructuredData />
      <div id="scroll-sentinel" className="absolute top-0 left-0 w-full h-[20px] pointer-events-none opacity-0 z-0" />
      <Header />
      <main id="main-content">
        <Hero />
        <AnnouncementNews />
        <AchievementsHome />
        <Academics />
        <Gallery />
        <Admissions />
        <Contact />
      </main>
      <Footer />
    </div>
  )
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <PublicDataLoader />
        <Popup />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-neutral-bg-light text-primary-dark">Loading...</div>}>
        <Routes>
                    {/* Main Website Route */}
                    <Route path="/" element={<MainWebsite />} />
                    
                    {/* Page Routes */}
                    <Route path="/contact-us" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <ContactUs />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/about-trust" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <AboutTrust />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/campus" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <CampusOverview />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/skill-based-activity" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <SkillBasedActivity />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/campus-directory" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <CampusDirectory />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/annual-planner" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <AnnualPlanner />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/academic-programs" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <AcademicPrograms />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/staff-details" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <StaffDetails />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/institutions" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <InstitutionsPage />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/events" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <EventsCelebrations />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/news" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <NewsUpdates />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/achievements" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <AchievementsAwards />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/social-activities" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <SocialActivities />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/alumni-corner" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <AlumniCorner />
                        </main>
                        <Footer />
                      </>
                    } />
                    <Route path="/alumni-stories" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <AlumniStories />
                        </main>
                        <Footer />
                      </>
                    } />
                    
                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    
                    {/* Protected Admin Routes */}
                    <Route path="/admin" element={
                      <ProtectedRoute>
                        <AdminLayout />
                      </ProtectedRoute>
                    }>
                      <Route index element={<Dashboard />} />
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="slider" element={<SliderManagement />} />
                      <Route path="gallery" element={<GalleryManagement />} />
                      <Route path="management" element={<ManagementTeam />} />
                      <Route path="staff" element={<StaffManagement />} />
                      <Route path="admissions" element={<AdmissionManagement />} />
                      <Route path="important-dates" element={<ImportantDates />} />
                      <Route path="messages" element={<Messages />} />
                      <Route path="news" element={<NewsManagement />} />
                      <Route path="announcements" element={<AnnouncementManagement />} />
                      <Route path="achievements" element={<AchievementsManagement />} />
                      <Route path="annual-planners" element={<AnnualPlannerManagement />} />
                      <Route path="campus-documents" element={<CampusDocumentManagement />} />
                      <Route path="alumni-network" element={<AlumniNetworkManagement />} />
                      <Route path="popups" element={<PopupManagement />} />
                      <Route path="*" element={<NotFound isAdmin />} />
                    </Route>

                    <Route path="*" element={
                      <>
                        <Header />
                        <main id="main-content" className="pt-20">
                          <NotFound />
                        </main>
                        <Footer />
                      </>
                    } />
                    </Routes>
                    </Suspense>
                  </Router>
    </HelmetProvider>
  )
}

export default App
